import * as THREE from 'three';
import { get, getWorldTransform } from '../modules/scene';
import getTargetWorldMatrix from './utils/getTargetWorldMatrix';
import updateCameraLookAtTarget from './utils/updateCameraLookAtTarget';
import { set } from '../modules/scene';
import { getBoundingBox, getCameraRadiusConstraint, getCamera, getCameraAttrs, setRect, getRect } from '../modules/player';
import { get as sceneGet, merge, virtualCameraId, virtualTargetId } from '../modules/sceneGraph';
import clamp from './utils/clamp';
import setMatrixOnNode from './utils/setMatrixOnNode';
import { updateSphericalRotation } from './tools/orbit';
import { updateZoom } from './tools/zoom';

const { degToRad, radToDeg } = THREE.Math;
const { tan, cos, atan, acos, min, max, pow, PI, sqrt } = Math;

// Temporary variables for intermediate calculations
const t_cameraPosition = new THREE.Vector3();
const t_oldTargetPosition = new THREE.Vector3();
const t_targetPosition = new THREE.Vector3();
const t_sphericalCamera = new THREE.Spherical();
const t_sphericalTarget = new THREE.Spherical();
const t_invTargetWorldMatrix = new THREE.Matrix4();
const t_invPivotMatrix = new THREE.Matrix4();
const t_changeQuaternion = new THREE.Quaternion();

export function resizeCamera(store, cameraId, rect) {
  const aspectRatio = rect.x / rect.y;
  store.dispatch(setRect(rect));
  const name = sceneGet(store, [cameraId, 'name']);
  if (name) store.dispatch(set({ id: cameraId, plug: 'Camera', property: 'aspectRatio' }, aspectRatio));
}

export function updateLookatTarget(store, cameraId, transformData, cameraData) {
  const playerData = get(store, { scene: true, evalPlug: 'Player' });
  const cameraWorldMatrix = getWorldTransform(store, cameraId);
  const targetWorldMatrix = getTargetWorldMatrix(store, cameraId);

  if (playerData.orbitMode === 'trackball') {
    t_cameraPosition.setFromMatrixPosition(cameraWorldMatrix);
    t_oldTargetPosition.set(0,0,-1).transformDirection(cameraWorldMatrix);
    t_targetPosition.setFromMatrixPosition(targetWorldMatrix).sub(t_cameraPosition).normalize();

    t_changeQuaternion.setFromUnitVectors(t_targetPosition, t_oldTargetPosition);
    cameraData.upPosition.applyQuaternion(t_changeQuaternion);
  }

  t_targetPosition.setFromMatrixPosition(targetWorldMatrix);
  cameraData.pivotMatrix.setPosition(t_targetPosition.sub(cameraData.offset));

  updateCameraLookAtTarget(cameraWorldMatrix, targetWorldMatrix, cameraData.upPosition);
  return cameraWorldMatrix;
}

export function initCamera(store, dispatch, fromCameraId, toCameraId, fromRect) {
  const toId = toCameraId || virtualCameraId;
  const fromId = fromCameraId || virtualCameraId;
  const boundingBox = getBoundingBox(store);
  const rect = fromRect || getRect(store);
  const cameraWorldMatrix = getWorldTransform(store, fromId);
  const targetWorldMatrix = getTargetWorldMatrix(store, fromId);
  const cameraPosition = new THREE.Vector3().setFromMatrixPosition(cameraWorldMatrix);

  resizeCamera(store, toId, rect);

  const playerData = get(store, { scene: true, evalPlug: 'Player' });
  const fromCameraData = get(store, { id: fromId, evalPlug: 'Camera' });
  const toCameraData = fromId === toId ? fromCameraData : get(store, { id: toId, evalPlug: 'Camera' });

  const pivotMatrix = targetWorldMatrix.clone();
  const pivotPosition = new THREE.Vector3().setFromMatrixPosition(pivotMatrix);
  cameraPosition.setFromMatrixPosition(cameraWorldMatrix).sub(pivotPosition);

  const upPosition = new THREE.Vector3(0, 1, 0);
  if (playerData.orbitMode === 'trackball') {
    upPosition.transformDirection(cameraWorldMatrix);

    const normal = new THREE.Vector3().copy(cameraPosition).normalize();
    const cameraViewPlane = new THREE.Plane(normal, 0);
    upPosition.copy(cameraViewPlane.projectPoint(upPosition).normalize());
  }

  const pivY = pivotMatrix.elements[13];
  if (pivY + cameraPosition.length() < 0 && playerData.constraintCameraY) {
    cameraPosition.normalize().multiplyScalar(pivY);
  }

  cameraWorldMatrix.setPosition(cameraPosition.add(pivotPosition));

  toCameraData.upPosition = upPosition;
  toCameraData.pivotMatrix = pivotMatrix;
  toCameraData.offset = new THREE.Vector3(0, 0, 0);

  adjustAngleAndRadius(store, fromId, cameraWorldMatrix, targetWorldMatrix, fromCameraData, boundingBox);

  if (playerData.constraintCameraY) updateSphericalRotation(store, toId, cameraWorldMatrix, targetWorldMatrix, new THREE.Vector2(0, 0));

  const radiusConstraint = getCameraRadiusConstraint(store);
  if (radiusConstraint) {
    initCameraRadiusConstraint(store, toId, toCameraData, radiusConstraint, cameraWorldMatrix, targetWorldMatrix);
  }

  updateCameraLookAtTarget(cameraWorldMatrix, targetWorldMatrix, upPosition);
  if (dispatch) setMatrixOnNode(store, toId, cameraWorldMatrix);
  return cameraWorldMatrix;
}

export function initCameraRadiusConstraint(store, cameraId, cameraData, outerRadius, initCameraWorldMatrix, initTargetWorldMatrix) {
  const boundingBox = getBoundingBox(store);
  const minBoundingSphere = new THREE.Sphere();
  const cameraWorldMatrix = initCameraWorldMatrix || getWorldTransform(store, cameraId);
  const targetWorldMatrix = initTargetWorldMatrix || getTargetWorldMatrix(store, cameraId);
  const playerData = get(store, { scene: true, evalPlug: 'Player' });

  boundingBox.getBoundingSphere(minBoundingSphere);
  if (outerRadius <= minBoundingSphere.radius) {
    return;
  }

  const maxBoundingSphere = new THREE.Sphere(minBoundingSphere.center, outerRadius);

  t_targetPosition.setFromMatrixPosition(targetWorldMatrix);
  t_cameraPosition.setFromMatrixPosition(cameraWorldMatrix);
  const targetToCamera = new THREE.Vector3().subVectors(t_cameraPosition, t_targetPosition);

  const thirdVector = new THREE.Vector3().crossVectors(targetToCamera, cameraData.upPosition).normalize();
  const newTargetPosition = minBoundingSphere.clampPoint(t_targetPosition);
  const newCameraPosition = maxBoundingSphere.clampPoint(t_cameraPosition);
  cameraWorldMatrix.setPosition(newCameraPosition);
  targetWorldMatrix.setPosition(newTargetPosition);

  if (playerData.orbitMode === 'trackball') {
    const newTargetToCamera = new THREE.Vector3().subVectors(newCameraPosition, newTargetPosition);
    cameraData.upPosition.crossVectors(thirdVector, newTargetToCamera).normalize();
  }

  cameraData.radiusConstraint = { outerRadius, maxBoundingSphere, minBoundingSphere };
  cameraData.newRadiusConstraint = null;
  updateZoom(store, 0, cameraId, cameraWorldMatrix);

  if (!initCameraWorldMatrix) setMatrixOnNode(store, cameraId, cameraWorldMatrix);
}

/*
 * Set the camera angle and radius.
 */
function adjustAngleAndRadius(store, cameraId, cameraWorldMatrix, targetWorldMatrix, cameraData, boundingBox) {
  let  { azimuthAngle, polarAngle, boundingRadius } = getCameraAttrs(store).toJS();
  const initialMinPolarAngle = 0;
  const initialMaxPolarAngle = PI;
  let minPolarAngle = 0;
  let maxPolarAngle = PI;

  if (cameraId === virtualCameraId) {
    // Use defaults for the virtual camera only
    if (azimuthAngle == null) azimuthAngle = 45;
    if (polarAngle == null) polarAngle = 20;
    if (boundingRadius == null) boundingRadius = 1;
  }

  if (azimuthAngle == null && polarAngle == null) {
    return;
  }

  t_invTargetWorldMatrix.getInverse(targetWorldMatrix, true);
  // world to relative
  t_cameraPosition.setFromMatrixPosition(cameraWorldMatrix).applyMatrix4(t_invTargetWorldMatrix);

  t_sphericalCamera.setFromVector3(t_cameraPosition);
  t_sphericalCamera.theta = degToRad(azimuthAngle);
  t_sphericalCamera.phi = degToRad(90 - polarAngle);

  minPolarAngle = max(minPolarAngle || 0, initialMinPolarAngle); // radians
  maxPolarAngle = min(maxPolarAngle || PI, initialMaxPolarAngle); // radians

  t_sphericalCamera.phi = clamp(t_sphericalCamera.phi, minPolarAngle, maxPolarAngle);

  if (boundingRadius != null) {
    const fixRadius = boundingRadius * boundingBox.getBoundingSphere().radius;
    t_sphericalCamera.radius = (fixRadius <= 0 ? 1 : fixRadius) / tan(degToRad(cameraData.fieldOfView * 0.5));
    t_sphericalCamera.radius = (fixRadius <= 0 || fixRadius === Infinity ? 1 : fixRadius) / tan(degToRad(cameraData.fieldOfView * 0.5));
  }

  t_sphericalCamera.makeSafe();

  t_cameraPosition.setFromSpherical(t_sphericalCamera);
  t_sphericalCamera.setFromVector3(t_cameraPosition);

  // relative to world
  t_cameraPosition.applyMatrix4(targetWorldMatrix);
  cameraWorldMatrix.setPosition(t_cameraPosition);
}



// WEBPACK FOOTER //
// ./src/translators/camera.js